'use client'

import Link from 'next/link'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
  ExternalLink,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import AnswerEngineOverallScore from './AnswerEngineOverallScore'
import ProviderCitationGrid from './ProviderCitationGrid'
import PlatformCTA from '@/components/platform/PlatformCTA'
import { PartialCoverageBanner } from '@/components/tools/result/PartialCoverageBanner'
import { readAnswerEngineSources } from '@/lib/radar/answer-engine-result'

interface ProviderResult {
  provider: string
  available: boolean
  /** Brand named in the prose answer. Independent of any cited URL. */
  brandMentioned?: boolean
  /** A provider-returned source URL on the target domain. */
  directCitationUrl?: string | null
  otherSourceUrls?: string[]
  verifiedCompetitorUrls?: string[]
  contentAlignmentScore: number
  snippet: string
  /** Historical records only; read through readProviderCitation. */
  cited?: boolean
  citedUrl?: string | null
  competitorUrls?: string[]
  error?: string
}

interface ScoreCategory {
  category: string
  score: number
  maxScore: number
  details: string
}

interface AnswerEngineResult {
  url: string
  domain: string
  question: string
  checkedAt: string
  overallScore: number | null
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | null
  scoreStatus?: 'graded' | 'withheld'
  pageTitle: string
  pageDescription: string
  providers: ProviderResult[]
  otherSourceUrls?: string[]
  verifiedCompetitorUrls?: string[]
  /** Historical records only; read through readAnswerEngineSources. */
  competitorUrls?: string[]
  scoreBreakdown: ScoreCategory[]
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

interface AnswerEngineResultsProps {
  result: AnswerEngineResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (`PlatformCTA` + the gradient services CTA) so the SAME
   * rich report can be reused inside `/platform/app`. Result content (question
   * callout, provider citation grid, score breakdown, competitor URLs, recs) is
   * identical either way.
   */
  variant?: 'public' | 'dashboard'
}

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

const CATEGORY_COLORS: Record<string, string> = {
  'Direct Citation': '#22c55e',
  'Content Alignment': '#008dcc',
  'Competitor Gap': '#F90B8A',
  'Cross-Platform Consistency': '#8b5cf6',
  'Answer Quality Match': '#FDC304',
}

export default function AnswerEngineResults({
  result,
  variant = 'public',
}: AnswerEngineResultsProps) {
  const hasUsableScore =
    result.scoreStatus !== 'withheld' &&
    result.overallScore !== null &&
    result.grade !== null
  const sources = readAnswerEngineSources(result)

  return (
    <div className='space-y-8'>
      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>Citation Test for</p>
            <p className='text-sm font-medium text-foreground truncate max-w-lg mx-auto'>
              {result.url}
            </p>
          </div>
          {hasUsableScore && (
            <AnswerEngineOverallScore
              score={result.overallScore!}
              grade={result.grade!}
            />
          )}

          {/* Question callout */}
          <div
            className='rounded-xl p-4 text-center'
            style={{
              backgroundColor: '#008dcc10',
              border: '1px solid #008dcc20',
            }}
          >
            <p className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1'>
              Question Tested
            </p>
            <p className='text-sm font-medium text-foreground'>
              &quot;{result.question}&quot;
            </p>
          </div>
        </div>
      </LenisFadeIn>

      {/* Provider Citation Grid */}
      <LenisFadeIn>
        <div>
          <h2 className='text-lg font-semibold text-foreground mb-4'>
            Provider Results
          </h2>
          {result.providers.some(p => !p.available) && (
            <div className='mb-4'>
              <PartialCoverageBanner
                respondedProviders={result.providers
                  .filter(p => p.available)
                  .map(p => p.provider)}
              />
            </div>
          )}
          <ProviderCitationGrid providers={result.providers} />
        </div>
      </LenisFadeIn>

      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='Answer Engine Tester'
          score={hasUsableScore ? result.overallScore! : undefined}
          grade={hasUsableScore ? result.grade! : undefined}
        />
      )}

      {/* AI Analysis */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Score Breakdown */}
      <LenisStaggered>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {result.scoreBreakdown.map(s => {
            const color = CATEGORY_COLORS[s.category] || '#6b7280'
            const pct =
              s.maxScore > 0 ? Math.round((s.score / s.maxScore) * 100) : 0
            return (
              <div
                key={s.category}
                className='bg-background border border-border/60 rounded-2xl p-5 flex flex-col gap-3'
              >
                <div className='flex items-center justify-between'>
                  <h3 className='text-sm font-semibold text-foreground'>
                    {s.category}
                  </h3>
                  <span
                    className='text-xs font-bold rounded-full px-2.5 py-0.5'
                    style={{ backgroundColor: `${color}15`, color }}
                  >
                    {s.score}/{s.maxScore}
                  </span>
                </div>
                <div className='h-2 rounded-full bg-muted overflow-hidden'>
                  <div
                    className='h-full rounded-full transition-all duration-700 ease-out'
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
                <p className='text-xs text-foreground/70'>{s.details}</p>
              </div>
            )
          })}
        </div>
      </LenisStaggered>

      {/* Verified competitors, when the caller supplied verified domains */}
      {sources.verifiedCompetitorUrls.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Verified Competitors Being Cited
            </h2>
            <p className='text-xs text-muted-foreground mb-3'>
              These URLs match competitor domains you supplied, and were cited
              instead of (or alongside) your page for this question.
            </p>
            <ul className='space-y-2'>
              {sources.verifiedCompetitorUrls.slice(0, 10).map((url, i) => (
                <li
                  key={i}
                  className='flex items-center gap-2 text-sm text-foreground/80'
                >
                  <ExternalLink
                    size={14}
                    className='shrink-0 text-accent-pink'
                  />
                  <span className='leading-none truncate'>{url}</span>
                </li>
              ))}
            </ul>
          </div>
        </LenisFadeIn>
      )}

      {/* Other cited sources. Sources, not competitors. */}
      {sources.otherSourceUrls.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Other Sources Cited
            </h2>
            <p className='text-xs text-muted-foreground mb-3'>
              These URLs were cited alongside (or instead of) your page for this
              question. They are sources, not verified competitors: directories,
              marketplaces, publications and social profiles land here too.
            </p>
            <ul className='space-y-2'>
              {sources.otherSourceUrls.slice(0, 10).map((url, i) => (
                <li
                  key={i}
                  className='flex items-center gap-2 text-sm text-foreground/80'
                >
                  <ExternalLink
                    size={14}
                    className='shrink-0 text-accent-pink'
                  />
                  <span className='leading-none truncate'>{url}</span>
                </li>
              ))}
            </ul>
          </div>
        </LenisFadeIn>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Citation Action Plan
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #991b1b, #dc2626, #ef4444)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Not getting cited? We can fix that.
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              We optimize page structure, structured data, and content
              formatting so AI search engines cite your pages instead of your
              competitors.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#dc2626'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get a Citation Strategy
              </a>
              <Link
                href='/tools/aeo-page-auditor'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Run AEO Audit <ArrowRight size={16} className='ml-1' />
              </Link>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
